import React, { useState } from "react";
import { getApiUrl } from "../../config/api";
import { CloseIcon, EyeIcon } from "../components/AdminIcons";

const Users = ({ allUsers, currentUser, setNotification }) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ username: '', email: '', newPassword: '' });
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const openEditModal = (user) => {
    if (user.id !== currentUser.id) return;
    setEditingUser(user);
    setFormData({ username: user.username || '', email: user.email || '', newPassword: '' });
    setValidationErrors({});
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingUser(null);
    setFormData({ username: '', email: '', newPassword: '' });
    setValidationErrors({});
  };

  const totalPages = Math.ceil(allUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = allUsers.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setValidationErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (formData.username) {
      if (formData.username.length < 5 || formData.username.length > 20) {
        errors.username = 'Username must be between 5 and 20 characters';
      } else if (!/^[a-zA-Z0-9]+$/.test(formData.username)) {
        errors.username = 'Username can only contain letters and numbers (no spaces or special characters)';
      }
    }
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = 'Please enter a valid email address (e.g., user@example.com)';
      }
    }
    if (formData.newPassword) {
      if (formData.newPassword.length < 8) {
        errors.newPassword = 'Password must be at least 8 characters long';
      } else if (formData.newPassword.length > 100) {
        errors.newPassword = 'Password is too long (maximum 100 characters)';
      }
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setNotification({ type: 'error', message: 'Please fix the errors in the form before submitting' });
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setNotification({ type: 'error', message: 'You are not logged in. Please log in again.' });
        setLoading(false);
        return;
      }
      const payload = {};
      if (formData.username && formData.username !== currentUser.username) payload.username = formData.username.trim();
      if (formData.email && formData.email !== currentUser.email) payload.email = formData.email.trim();
      if (formData.newPassword && formData.newPassword.trim()) payload.newPassword = formData.newPassword;

      if (Object.keys(payload).length === 0) {
        setNotification({ type: 'info', message: 'No changes were made' });
        setLoading(false);
        return;
      }

      const url = getApiUrl('UPDATE_USER', { userId: editingUser.id });
      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (response.ok) {
        setNotification({ type: 'success', message: data.message || 'Your profile has been updated successfully!' });
        const updatedUser = { ...currentUser, username: payload.username || currentUser.username, email: payload.email || currentUser.email };
        localStorage.setItem('adminUser', JSON.stringify(updatedUser));
        closeEditModal();
        setTimeout(() => { window.location.reload(); }, 1000);
      } else {
        let errorMessage = 'Failed to update profile';
        if (response.status === 400) errorMessage = data.message || 'Invalid input. Please check your data and try again.';
        else if (response.status === 403) errorMessage = 'You do not have permission to update this profile.';
        else if (response.status === 404) errorMessage = 'User not found. Please refresh and try again.';
        else if (response.status === 500) errorMessage = 'Server error occurred. Please try again later or contact support.';
        else errorMessage = data.message || 'An unexpected error occurred. Please try again.';
        setNotification({ type: 'error', message: errorMessage });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      let errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
      if (error.message.includes('Failed to fetch')) errorMessage = 'Network error: Cannot reach the server. Please check if the server is running.';
      else if (error.name === 'TypeError') errorMessage = 'A technical error occurred. Please refresh the page and try again.';
      setNotification({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">Users</h1>
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-red-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Username</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Created At</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedUsers && paginatedUsers.length > 0 ? (
                paginatedUsers.map((user) => (
                  <tr key={user.id} className={user.id === currentUser.id ? 'bg-yellow-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm font-medium text-gray-900">{user.username}</div></td>
                    <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-500">{user.email}</div></td>
                    <td className="px-6 py-4 whitespace-nowrap"><span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Admin</span></td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {user.id === currentUser.id ? (
                        <button onClick={() => openEditModal(user)} className="text-indigo-600 hover:text-indigo-900">Edit Profile</button>
                      ) : (
                        <span className="text-gray-400">No Access</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="5" className="px-6 py-4 text-center text-gray-500">No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {allUsers.length > itemsPerPage && (
        <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-4 rounded-lg shadow-md">
          <div className="text-sm text-gray-600">
            Showing <span className="font-semibold">{startIndex + 1}</span> to <span className="font-semibold">{Math.min(endIndex, allUsers.length)}</span> of <span className="font-semibold">{allUsers.length}</span> users
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-end">
            <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">← Previous</button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => {
                const page = i + 1;
                const isVisible = page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                if (!isVisible && page > 1 && page < totalPages) {
                  if (page === 2 || page === totalPages - 1) return null;
                  if ((page === 3 && currentPage > 3) || (page === totalPages - 2 && currentPage < totalPages - 2)) {
                    return <span key={`ellipsis-${page}`} className="px-1">...</span>;
                  }
                  return null;
                }
                return (
                  <button key={page} onClick={() => goToPage(page)} className={`px-3 py-2 rounded transition-colors ${currentPage === page ? 'bg-red-700 text-white font-semibold' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>{page}</button>
                );
              }).filter(Boolean)}
            </div>
            <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Next →</button>
          </div>
        </div>
      )}

      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Edit Profile</h2>
              <button onClick={closeEditModal} className="text-gray-500 hover:text-gray-700" aria-label="Close modal"><CloseIcon /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Username <span className="text-red-500">*</span></label>
                <input type="text" name="username" value={formData.username} onChange={handleFormChange} className={`shadow-sm appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 ${validationErrors.username ? 'border-red-500 focus:ring-red-500' : 'focus:ring-yellow-500'}`} required />
                {validationErrors.username && <p className="text-red-500 text-xs mt-1">{validationErrors.username}</p>}
                {!validationErrors.username && <p className="text-xs text-gray-500 mt-1">5-20 characters, letters and numbers only</p>}
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Email <span className="text-red-500">*</span></label>
                <input type="email" name="email" value={formData.email} onChange={handleFormChange} className={`shadow-sm appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 ${validationErrors.email ? 'border-red-500 focus:ring-red-500' : 'focus:ring-yellow-500'}`} required />
                {validationErrors.email && <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>}
              </div>
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600 mb-3 font-semibold">Change Password (Optional)</p>
                <p className="text-xs text-gray-500 mb-3">Leave blank to keep current password</p>
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <div>
                      <span className="text-sm text-blue-800 font-medium">Your password is encrypted and secure</span>
                      <p className="text-xs text-blue-600 mt-1">Only you can change your password - no verification needed</p>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">New Password</label>
                  <div className="relative">
                    <input type={showNewPassword ? 'text' : 'password'} name="newPassword" value={formData.newPassword} onChange={handleFormChange} placeholder="Enter new password (if changing)" className={`shadow-sm appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 ${validationErrors.newPassword ? 'border-red-500 focus:ring-red-500' : 'focus:ring-yellow-500'}`} />
                    <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-2 top-2 text-gray-500 hover:text-gray-800" aria-label={showNewPassword ? 'Hide password' : 'Show password'}>
                      <EyeIcon open={showNewPassword} />
                    </button>
                  </div>
                  {validationErrors.newPassword && <p className="text-red-500 text-xs mt-1">{validationErrors.newPassword}</p>}
                  {!validationErrors.newPassword && <p className="text-xs text-gray-500 mt-1">Minimum 8 characters (leave blank to keep current)</p>}
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={closeEditModal} className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors" disabled={loading}>Cancel</button>
                <button type="submit" className="px-4 py-2 bg-red-700 text-white rounded hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" disabled={loading}>
                  {loading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Updating...
                    </span>
                  ) : 'Update Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
